/* @flow */
import React, { Component } from "react";
import styled, { css, ThemeConsumer } from "styled-components";

import Scrollable from "../common/Scrollable";
import Gettext, { withGettext } from "../common/Gettext";
import ToggleTool from "./ToggleTool";
import HideTool from "./HideTool";
import LockTool from "./LockTool";
import ColorGrid from "../Popup/ColorGrid";
import ThicknessGrid from "../Popup/ThicknessGrid";
import {normalHistoryIntervals, HumanInterval} from "../TradeModals/IntervalsModal";
import * as Helpers from "../Chart/NextGenChartMath";
import {
  DeleteIcon,
  ChartIconZone,
  ChartIconCandlestick,
  ChartIconBars,
  HorizontalLineIcon,
  AddIcon,
  TrashIcon
} from "./Icons";

import type { IndicatorConfig } from "../../redux/reducer/settings";
import { gaReducer } from "../GA";

const CoolTool = styled.div`
  height: 20px;
  width: 34px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const CoolToolMool = styled.div`
  width: 100%;
  height: ${props => props.width}px;
  background-color: ${props => props.color};
  margin: auto;
  border-radius: 5px;
`;

const EditLiner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  margin: 8px;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const IconContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 9px;
  width: 24px;
  height: 24px;
  color: inherit;
  pointer-events: none;
`;

const Option = styled.div`
  position: relative;
  min-height: 30px;
  padding: 5px 9px;
  background-color: ${props => props.theme.mainHighlight};
  margin-bottom: 8px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  line-height: 14px;
  color: #99959d;
  cursor: pointer;
  display: flex;
  align-items: center;

  :focus {
    color: ${({ theme }) => theme.accent};
  }

  :hover,
  :active {
    color: ${props => props.theme.accent};
  }

  ${props =>
    props.isActive &&
    css`
      color: ${({ theme }) => theme.accent};
    `};
`;

const ThemeOption = styled(Option)`
  padding-right: 35px;
`;

const RemoveIndicesOption = styled(Option)`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  min-height: auto;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  line-height: 16px;
  background: transparent;

  @media (max-width: 345px) {
    align-self: flex-end;
  }
`;

/* prettier-ignore */
const ChartTypeOption = styled(Option)`
  padding-left: 40px;
  padding-right: 35px;
`;

const OptionInlineContainer = styled.div`
  direction: ltr;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  align-content: space-between;
  width: 100%;

  :after {
    content: "";
    flex: 1;
  }

  @supports (display: grid) {
    display: grid;
    grid-template-columns: repeat(${props => props.col || 4}, 1fr);
    grid-column-gap: 8px;

    :after {
      display: none;
    }

    @media (max-width: 479px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;

const OptionInline = styled(Option)`
  flex-grow: 0;
  flex-shrink: 1;
  flex-basis: calc(1 / 4 * 100% - (1 - 1 / 4) * 8px);
  padding-left: 13px;
  text-transform: lowercase;

  :not(:nth-of-type(4n + 1)) {
    margin-left: 8px;
  }

  @media (max-width: 479px) {
    flex-basis: calc(1 / 2 * 100% - (1 - 1 / 2) * 8px);

    :not(:nth-of-type(4n + 1)) {
      margin-left: 0;
    }
    :not(:nth-of-type(2n + 1)) {
      margin-left: 8px;
    }
  }

  @supports (display: grid) {
    :not(:nth-of-type(4n + 1)) {
      margin-left: 0;
    }
    :not(:nth-of-type(2n + 1)) {
      margin-left: 0;
    }
  }
`;

const IndeciesOption = styled(Option)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding-left: 9px;

  :focus {
    color: ${({ theme }) => theme.foreground};
  }

  :hover,
  :active {
    color: ${props => props.theme.accent};
  }

  @media (hover: none) {
    :focus,
    :hover,
    :active {
      color: #99959d;
    }
  }
`;

const IndeciesTitle = styled.div`
  margin-right: ${({ theme }) => (theme.locale === "ar" ? 24 : 0)}px;
  color: inherit;
`;

const ChosenSvg = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 9px;
  display: flex;
  align-items: center;
  color: rgb(153, 149, 157);
`;

const SwitchWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${props =>
    props.sentimentsWrapper &&
    css`
      @media (min-width: 480px) {
        height: 100%;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;
      }
      @media (max-width: 479px) {
        padding-top: 15px;
      }
    `};

  ${props =>
    props.chartWrapper &&
    css`
      @media (min-width: 480px) {
        flex-direction: row;
        align-items: center;
      }

      @media (max-width: 479px) {
        margin-top: 20px;
      }
    `};
`;

const SwitchHeading = styled.div`
  direction: ${props => props.theme.direction};
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  line-height: 1.5;
  text-align: center;
  color: ${({ theme }) => theme.foreground};

  @media (min-width: 480px) {
    flex-grow: ${props => (props.theme.direction === "ltr" ? 1 : 0)};
    text-align: ${props =>
      props.theme.direction === "ltr" ? "left" : "right"};
  }
  @media (max-width: 479px) {
    padding-bottom: 15px;
  }
`;

const Color = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 1;
`;

const ActiveIndicators = styled.div`
  > div {
    border-bottom: 1px solid #4c4555;
  }

  > div:first-of-type {
    margin-top: -8px;
  }

  > div:last-of-type {
    border-bottom: none;
  }
`;

const ActiveIndicator = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding-right: ${props => (props.noPadding ? 0 : "9px")};
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "12px"};
  color: #99959d;
`;

const IndicatorInfo = styled.div`
  direction: ltr;
  display: flex;
  align-items: center;
  padding: ${props => (props.noPadding ? 0 : "10px 5px")};
  cursor: pointer;
  opacity: ${props => (props.enabled ? 1 : 0.7)};
`;

const IndicatorControls = styled.div`
  display: flex;
`;

const Colors = styled.div`
  width: 15px;
  height: 15px;
  margin-right: 8px;
`;

const IndicatorColor = styled(Colors)`
  background-color: ${props => props.color};
`;

const TwoColors = styled(Colors)`
  background: linear-gradient(
    to right,
    ${props => props.colors[0]} 0%,
    ${props => props.colors[0]} 50%,
    ${props => props.colors[1]} 50.1%,
    ${props => props.colors[1]} 100%
  );
`;

const ThreeColors = styled(Colors)`
  background: linear-gradient(
    to right,
    ${props => props.colors[0]} 0%,
    ${props => props.colors[0]} 33.3%,
    ${props => props.colors[1]} 33.4%,
    ${props => props.colors[1]} 66.6%,
    ${props => props.colors[2]} 67.7%,
    ${props => props.colors[2]} 100%
  );
`;

const RemoveText = styled.div`
  margin-right: 5px;

  ${props =>
    props.theme.direction === "rtl" &&
    css`
      margin-right: 0;
      margin-left: 5px;
    `};
`;

const ActiveTools = styled.div`
  padding-right: 10px;
`;

const ActiveTool = styled.div`
  height: 24px;
  margin: 4px;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
`;

export const CheckedIcon = ({ style }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="13"
    viewBox="0 0 18 13"
    style={style}
  >
    <path
      fillRule="evenodd"
      fill="currentColor"
      d="M17.124,1.659 L7.379,11.404 L7.391,11.416 L6.239,12.568 L0.190,6.519 L1.342,5.367 L6.227,10.252 L15.973,0.507 L17.124,1.659 Z"
    />
  </svg>
);

const ChosenOption = () => (
  <ChosenSvg>
    <CheckedIcon />
  </ChosenSvg>
);

const ActiveItem = ({
  children,
  inHead,
  enabled,
  toggleTitle,
  onRemove,
  onClick,
  onToggle
}) => (
  <ActiveIndicator noPadding={inHead}>
    <IndicatorInfo enabled={enabled} onClick={onClick} noPadding={inHead}>
      {children}
    </IndicatorInfo>

    <IndicatorControls>
      <HideTool
        value={enabled}
        onClick={onToggle}
        style={{
          marginRight: 5,
          marginTop: -1
        }}
      />
      <Remove onClick={onRemove} />
    </IndicatorControls>
  </ActiveIndicator>
);

// Каждый инструмент работает как контролируемый инпут.
// Нужно дать ему текущее значение и функцию чтобы его менять.
type ToolProps<TValue> = {
  value: TValue,
  onChange: (value: TValue) => void
};

// Инструмент для смены типа графика
const CHART_TYPES = [
  {
    key: "line",
    title: <Gettext>Zone Chart</Gettext>,
    Icon: ChartIconZone
  },
  {
    key: "candlestick",
    title: <Gettext>Candlestick Chart</Gettext>,
    Icon: ChartIconCandlestick
  },
  {
    key: "bar",
    title: <Gettext>Bars Chart</Gettext>,
    Icon: ChartIconBars
  }
];

export function SwitchChartType({ value, onChange, animated, onChangeAnimated }: ToolProps<string>) {
  return <React.Fragment>
    {CHART_TYPES.map(tool => {
      const { title, Icon, key } = tool;

      return (
        <ChartTypeOption
          key={key}
          onClick={e => {
            gaReducer("CLICKBTN_CHART_TYPE", {
              buttonText: e.currentTarget.textContent
            });
            onChange(key);
          }}
          isActive={value === key}
        >
          {Icon && (
            <IconContainer>
              <Icon isActive={value === key} isTool={true} />
            </IconContainer>
          )}{" "}
          {title}
          {value === key && <ChosenOption />}
        </ChartTypeOption>
      );
    })}

    <SwitchWrapper chartWrapper>
      <SwitchHeading>
        <Gettext>Chart animation</Gettext>
      </SwitchHeading>
      <ToggleTool value={animated} onChange={onChangeAnimated} />
    </SwitchWrapper>
  </React.Fragment>
}

// Инструмент выбора толщины линии
const LINE_THICKNESSES = [1, 2, 3, 4, 5];

export function SwitchLineThickness({ value, onChange }: ToolProps<number>) {
  return (
    <OptionInlineContainer>
      {LINE_THICKNESSES.map(thickness => (
        <OptionInline
          key={thickness}
          isActive={thickness === value}
          onClick={e => {
            gaReducer("CLICKBTN_LINE", {
              buttonText: e.currentTarget.textContent
            });
            onChange(thickness);
          }}
        >
          {thickness}
          px
          {thickness === value && <ChosenOption />}
        </OptionInline>
      ))}
    </OptionInlineContainer>
  );
}

export function SwitchInterval({ value, onChange }: ToolProps<number>) {

  return (
    <OptionInlineContainer col={3}>
      {normalHistoryIntervals.map(int => (
        <OptionInline
          key={int}
          isActive={Helpers.parseTime(int) / 1000 === value}
          onClick={e => {
            gaReducer("CLICKBTN_INTERVAL", {
              buttonText: e.currentTarget.textContent
            });
            onChange(Helpers.parseTime(int) / 1000);
          }}
        >
          <HumanInterval>{int}</HumanInterval>
          {Helpers.parseTime(int) / 1000 === value && <ChosenOption />}
        </OptionInline>
      ))}
    </OptionInlineContainer>
  );
}

// Инструмент для активации индикаторов
const INDICATORS = [
  {
    key: "RSI",
    title: <Gettext>RSI (Relative Strength Index)</Gettext>,
    displayColors: indicator => [indicator.color],
    displayInfo: indicator =>
      `(${indicator.period}; ${indicator.algorithm}) ${indicator.strokeWidth}px`
  },
  {
    key: "ATR",
    title: <Gettext>ATR (Average True Range)</Gettext>,
    displayColors: indicator => [indicator.color],
    displayInfo: indicator => `(${indicator.period}) ${indicator.strokeWidth}px`
  },
  {
    key: "MACD",
    title: <Gettext>MACD</Gettext>,
    displayColors: indicator =>
      indicator.likeAPro
        ? [indicator.color]
        : [indicator.color, indicator.macdColor],
    displayInfo: indicator =>
      `(${indicator.fastPeriod}; ${indicator.slowPeriod}; ${
        indicator.period
      }) ${indicator.strokeWidth}px ${
        indicator.likeAPro ? "" : indicator.macdWidth + "px"
      }`
  },
  {
    key: "HeikenAshi",
    title: <Gettext>Heiken-Ashi</Gettext>,
    displayColors: () => [],
    displayInfo: () => <Gettext>Heiken-Ashi</Gettext>
  },
  {
    key: "ROC",
    title: <Gettext>ROC (Rate of Change)</Gettext>,
    displayColors: indicator => [indicator.color],
    displayInfo: indicator => `(${indicator.period}) ${indicator.strokeWidth}px`
  },
  {
    key: "Stoch",
    title: <Gettext>Stochastic Oscillator</Gettext>,
    displayColors: indicator => [indicator.colorK, indicator.colorD],
    displayInfo: indicator =>
      `(${indicator.periodK}; ${indicator.periodD}; ${indicator.slowing}) ${
        indicator.strokeWidth
      }px`
  },
  {
    key: "MovingAverage",
    title: <Gettext>Moving average</Gettext>,
    displayColors: indicator => [indicator.color],
    displayInfo: indicator =>
      `(${indicator.period}; ${indicator.algorithm}) ${indicator.strokeWidth}px`
  },
  {
    key: "ParabolicSAR",
    title: <Gettext>Parabolic SAR</Gettext>,
    displayColors: indicator => [indicator.color],
    displayInfo: indicator =>
      `(${indicator.accelerationStep}; ${indicator.accelerationMaxValue}) ${
        indicator.size
      }px`
  },
  {
    key: "Alligator",
    title: <Gettext>Alligator</Gettext>,
    displayColors: indicator => [
      indicator.jaws.color,
      indicator.teeths.color,
      indicator.lips.color
    ],
    displayInfo: indicator =>
      `(${indicator.jaws.period}; ${indicator.teeths.period}; ${
        indicator.lips.period
      }) ${indicator.jaws.strokeWidth}px ${indicator.teeths.strokeWidth}px ${
        indicator.lips.strokeWidth
      }px`
  },
  {
    key: "BollingerLines",
    title: <Gettext>Bollinger Lines</Gettext>,
    displayColors: indicator => [
      indicator.upper.color,
      indicator.middle.color,
      indicator.lower.color
    ],
    displayInfo: indicator =>
      `(${indicator.upper.period}; ${indicator.upper.rejection}) ${
        indicator.upper.strokeWidth
      }px ${indicator.middle.strokeWidth}px ${indicator.lower.strokeWidth}px`
  }
];

type ActivateIndicatorProps = {
  indicators: Array<IndicatorConfig>,
  handlePopupOpen: (key: string) => void
};

class ActivateIndicatorWithoutGettext extends Component<
  ActivateIndicatorProps
> {
  render() {
    let HeikenTools;

    const heikenIndex = this.props.indicators.findIndex(
      indicator => indicator.type === "HeikenAshi"
    );
    const heiken = heikenIndex > -1 && this.props.indicators[heikenIndex];

    if (heiken) {
      HeikenTools = (
        <ActiveItem
          key={heikenIndex}
          enabled={heiken.enabled !== false}
          toggleTitle={this.props.gettext`Toggle`}
          onClick={() => this.props.handlePopupOpen("HeikenAshi", heiken)}
          onToggle={() => this.props.onToggle(heikenIndex)}
          onRemove={() => this.props.onRemove(heikenIndex)}
          inHead
        >
          <Gettext>Heiken-Ashi</Gettext>
        </ActiveItem>
      );
    }

    return INDICATORS.map((indicator, index) => {
      const { key, title, displayColors, displayInfo } = indicator;

      return (
        <React.Fragment key={index}>
          <IndeciesOption
            key={key}
            active={false}
            onClick={e => {
              gaReducer("CLICKBTN_INDICATOR", {
                buttonText: e.currentTarget.textContent
              });

              return this.props.handlePopupOpen(key);
            }}
          >
            {key === "HeikenAshi" && heiken ? (
              HeikenTools
            ) : (
              <React.Fragment>
                <IndeciesTitle>{title}</IndeciesTitle>
                <ChosenSvg as={AddIcon} />
              </React.Fragment>
            )}
          </IndeciesOption>
          <ActiveIndicators>
            {this.props.indicators
              .map((indicator, id) => ({ ...indicator, id }))
              .filter(
                indicator => key !== "HeikenAshi" && indicator.type === key
              )
              .map((indicator, index) => {
                let ColorsIcon;
                const colors = displayColors(indicator);
                switch (colors.length) {
                  case 1:
                    ColorsIcon = <IndicatorColor color={colors[0]} />;
                    break;
                  case 2:
                    ColorsIcon = <TwoColors colors={colors} />;
                    break;
                  case 3:
                    ColorsIcon = <ThreeColors colors={colors} />;
                    break;
                  default:
                    ColorsIcon = null;
                }

                return (
                  <ActiveItem
                    key={index}
                    enabled={indicator.enabled !== false}
                    toggleTitle={this.props.gettext`Toggle`}
                    onClick={() => this.props.handlePopupOpen(key, indicator)}
                    onToggle={() => this.props.onToggle(indicator.id)}
                    onRemove={() => this.props.onRemove(indicator.id)}
                  >
                    {ColorsIcon}
                    {displayInfo(indicator)}
                  </ActiveItem>
                );
              })}
          </ActiveIndicators>
        </React.Fragment>
      );
    });
  }
}

export const ActivateIndicator = withGettext(ActivateIndicatorWithoutGettext);

export const HorizontalLine = props => {
  return (
    <React.Fragment>
      <IndeciesOption onClick={props.onClick}>
        <IndeciesTitle>
          {props.graphicalMode.graphicalTool ? (
            <Gettext>Click somewhere on the chart</Gettext>
          ) : (
            <FlexContainer>
              <HorizontalLineIcon
                isActive
                isChartButton
                style={{
                  marginRight: 10,
                  pointerEvents: "none"
                }}
              />
              <Gettext>Add line</Gettext>
            </FlexContainer>
          )}
        </IndeciesTitle>
        {props.graphicalMode.graphicalTool ? (
          <ChosenSvg as={DeleteIcon} />
        ) : (
          <ChosenSvg as={AddIcon} />
        )}
      </IndeciesOption>

      {props.editMode != null ? (
        <div>
          <EditLiner>
            <Gettext>Edit line</Gettext>
            <DeleteIcon onClick={() => props.onToolCoolClick()} />
          </EditLiner>

          <ColorGrid
            value={props.editMode.tool.color}
            onChange={color =>
              props.dispatch({
                type: "UPDATE_GRAPHICAL_TOOL",
                payload: {
                  stock: props.editMode.tool.stock,
                  index: props.editMode.index,
                  color
                }
              })
            }
          />

          <ThicknessGrid
            value={props.editMode.tool.width}
            color={props.editMode.tool.color}
            onChange={width =>
              props.dispatch({
                type: "UPDATE_GRAPHICAL_TOOL",
                payload: {
                  stock: props.editMode.tool.stock,
                  index: props.editMode.index,
                  width
                }
              })
            }
          />
        </div>
      ) : (
        <Scrollable style={{ maxHeight: 228 }}>
          <ActiveTools>
            {props.tools.map((tool, index) => {
              return (
                <ActiveTool key={index}>
                  <CoolTool onClick={() => props.onToolCoolClick(tool, index)}>
                    <CoolToolMool color={tool.color} width={tool.width} />
                  </CoolTool>

                  <LockTool
                    value={tool.locked === true}
                    onClick={value =>
                      props.dispatch({
                        type: "UPDATE_GRAPHICAL_TOOL",
                        payload: {
                          stock: tool.stock,
                          index,
                          locked: value
                        }
                      })
                    }
                  />
                  <HideTool
                    value={tool.enabled !== false}
                    onClick={value =>
                      props.dispatch({
                        type: "UPDATE_GRAPHICAL_TOOL",
                        payload: {
                          stock: tool.stock,
                          index,
                          enabled: value
                        }
                      })
                    }
                  />
                  <Remove
                    onClick={() =>
                      props.dispatch({
                        type: "REMOVE_GRAPHICAL_TOOL",
                        payload: {
                          stock: tool.stock,
                          index
                        }
                      })
                    }
                  />
                </ActiveTool>
              );
            })}
          </ActiveTools>
        </Scrollable>
      )}
    </React.Fragment>
  );
};

// Инструмент для выпила индикаторов
type RemoveIndicatorsProps = {
  indicators: Array<IndicatorConfig>,
  onRemove: (index: number) => void,
  onRemoveAll: () => void
};

export function RemoveIndicators(props: RemoveIndicatorsProps) {
  if (props.indicators && props.indicators.length > 0) {
    return (
      <RemoveIndicesOption key="rmv-all" onClick={props.onRemoveAll}>
        <RemoveText>
          <Gettext>Remove all</Gettext>
        </RemoveText>
        <TrashIcon />
      </RemoveIndicesOption>
    );
  } else {
    return null;
  }
}

export function Remove(props) {
  return (
    <RemoveIndicesOption onClick={props.onClick}>
      <TrashIcon />
    </RemoveIndicesOption>
  );
}

export function SwitchSentiments({ value, onChange }: ToolProps<boolean>) {
  return (
    <SwitchWrapper sentimentsWrapper>
      <SwitchHeading>
        <Gettext>Show indicator</Gettext>
      </SwitchHeading>
      <ToggleTool value={value} onChange={onChange} />
    </SwitchWrapper>
  );
}

export function SwitchLineColor({
  value,
  onChange
}: {
  ...ToolProps<string>,
  theme: Object
}) {
  return (
    <ThemeConsumer>
      {theme => (
        <OptionInlineContainer>
          {[
            "#feac00",
            "#00fe83",
            "#00cbfe",
            "#fe00e9",
            "#fe003c",
            "#39ae00",
            "#a100fe",
            "#fe4e00",
            "#afafaf",
            theme.foreground
          ].map(color => (
            <OptionInline
              key={color}
              isActive={color === value}
              onClick={() => {
                gaReducer("CLICKBTN_SETTINGS_COLOR");
                return onChange(color);
              }}
            >
              <Color style={{ backgroundColor: color }} />
              {color === value && <ChosenOption />}
            </OptionInline>
          ))}
        </OptionInlineContainer>
      )}
    </ThemeConsumer>
  );
}

// Инструмент выбора темы
const THEMES = [
  {
    title: <Gettext>Main</Gettext>,
    key: "default"
  },
  {
    title: <Gettext>Light</Gettext>,
    key: "light"
  },
  {
    title: <Gettext>Rich blue</Gettext>,
    key: "richblue"
  }
];

export function SwitchTheme({ value, onChange }: ToolProps<string>) {
  return THEMES.map(theme => {
    const isActive = theme.key === value;
    return (
      <ThemeOption
        key={theme.key}
        isActive={isActive}
        onClick={e => {
          gaReducer("CLICKBTN_THEME", {
            buttonText: e.currentTarget.textContent
          });
          onChange(theme.key);
        }}
      >
        {theme.title}
        {isActive && <ChosenOption />}
      </ThemeOption>
    );
  });
}

export function SwitchSound({ value, onChange }: ToolProps<boolean>) {
  return (
    <SwitchWrapper sentimentsWrapper>
      <SwitchHeading>
        <Gettext>Use sound effects to indicate different actions</Gettext>
      </SwitchHeading>
      <ToggleTool value={value} onChange={onChange} />
    </SwitchWrapper>
  );
}

// // инструмент фуллскрина
// export function ToggleFullscreen({ value, onClick }: ToolProps<number>) {
//   return (
//     <SwitchWrapper>
//       <ToggleSwitch value={value} onClick={onClick} />
//     </SwitchWrapper>
//   );
// }

// // Инструмент для смены языка
// const LANGUAGES = [
//   {
//     locale: "en",
//     title: "English"
//   },
//   {
//     locale: "th",
//     title: "Tiếng Thái"
//   }
// ];
//
// export function SwitchLanguage({ value, onChange }: ToolProps<string>) {
//   const languages = LANGUAGES.map(language =>
//     <Option
//       active={language.locale === value}
//       onClick={() => {
//         onChange(language.locale);
//       }}
//     >
//       {language.title}
//     </Option>
//   );
//
//   return (
//     <DropdownTool Icon={LanguageIcon} title={<Gettext>Language</Gettext>}>
//       {() => <div>{languages}</div>}
//     </DropdownTool>
//   );
// }

// // Инструмент для включения/выключения звука
// export function SwitchSound({ value, onChange }: ToolProps<boolean>) {
//   const icon = value
//     ? <SoundIcon onClick={() => onChange(false)} />
//     : <SoundIconMute onClick={() => onChange(true)} />;
//
//   return <ToggleTool>{icon}</ToggleTool>;
// }



// WEBPACK FOOTER //
// ./src/components/Toolbar/Tools.js